import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const DISCORD_ERRORS = {
  not_configured: "Discord sign-in isn't set up yet — ask the admin to add credentials.",
  state_mismatch: "Session expired during Discord sign-in. Try again.",
  token_exchange: "Discord refused the sign-in — please try again.",
  no_token: "Discord returned an empty token.",
  profile: "Couldn't read your Discord profile.",
  exception: "Something went wrong reaching Discord.",
  email_unverified: "Your Discord email must be verified before you can sign in.",
  email_conflict: "That Discord email is already linked to another account.",
};

export default function Login() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [discordEnabled, setDiscordEnabled] = useState(false);
  const [discordError, setDiscordError] = useState("");
  const [params] = useSearchParams();

  useEffect(() => {
    api.get("/auth/discord/enabled").then((r) => setDiscordEnabled(!!r.data?.enabled)).catch(() => setDiscordEnabled(false));
    const err = params.get("discord_error");
    if (err) {
      const msg = DISCORD_ERRORS[err] || "Discord sign-in failed";
      setDiscordError(msg);
      toast.error(msg);
    }
  }, [params]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = await login(email, password);
    setBusy(false);
    if (res.ok) {
      toast.success("Signal locked. Welcome back.");
      nav("/dashboard");
    } else {
      toast.error(res.error || "Login failed");
    }
  };

  const backend = process.env.REACT_APP_BACKEND_URL;

  return (
    <div className="max-w-md mx-auto py-12">
      <div className="text-cyan-400 font-mono tracking-[0.3em] text-xs mb-3">/ ACCESS TERMINAL</div>
      <h1 className="font-heading text-4xl font-black tracking-tighter uppercase mb-8">Log in</h1>

      {discordError && (
        <div
          data-testid="login-discord-error"
          className="mb-4 border border-red-500/40 bg-red-500/10 text-red-200 px-4 py-3 text-sm font-mono"
        >
          <div className="uppercase tracking-widest text-[10px] text-red-400 mb-1">Discord sign-in failed</div>
          {discordError}
        </div>
      )}

      {discordEnabled && (
        <div className="mb-4">
          <a
            href={`${backend}/api/auth/discord/start`}
            data-testid="login-discord-btn"
            className="w-full flex items-center justify-center gap-3 rounded-none bg-[#5865F2] hover:bg-[#4752c4] text-white font-bold uppercase tracking-widest py-3 border border-[#5865F2] transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M20.317 4.369A19.79 19.79 0 0 0 15.61 3l-.19.35a17.98 17.98 0 0 0-4.84 0L10.39 3a19.8 19.8 0 0 0-4.71 1.37C2.79 8.09 2.09 11.72 2.44 15.29a19.94 19.94 0 0 0 5.98 3.02l.48-.66a13 13 0 0 1-2.06-1c.17-.12.34-.25.5-.38a12.44 12.44 0 0 0 10.32 0c.16.13.33.26.5.38a13 13 0 0 1-2.07 1l.48.66a19.9 19.9 0 0 0 5.98-3.02c.4-4.16-.73-7.75-2.23-10.92ZM8.02 13.9c-1.18 0-2.15-1.08-2.15-2.4s.95-2.4 2.15-2.4c1.2 0 2.16 1.09 2.14 2.4 0 1.32-.95 2.4-2.14 2.4Zm7.96 0c-1.18 0-2.15-1.08-2.15-2.4s.95-2.4 2.15-2.4c1.2 0 2.16 1.09 2.14 2.4 0 1.32-.94 2.4-2.14 2.4Z" />
            </svg>
            Continue with Discord
          </a>
          <div className="flex items-center gap-3 my-4">
            <div className="flex-1 h-px bg-[#232330]" />
            <span className="text-[10px] font-mono uppercase tracking-widest text-gray-600">or with email</span>
            <div className="flex-1 h-px bg-[#232330]" />
          </div>
        </div>
      )}

      <form onSubmit={onSubmit} className="space-y-5 border border-[#232330] bg-[#101015] p-6">
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-widest text-gray-400">Email</Label>
          <Input
            data-testid="login-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="rounded-none bg-[#05050A] border-[#232330] focus:border-cyan-400 focus:ring-cyan-400/40"
          />
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-xs uppercase tracking-widest text-gray-400">Password</Label>
            <Link
              to="/forgot-password"
              data-testid="link-forgot"
              className="text-[10px] font-mono uppercase tracking-widest text-cyan-400 hover:underline"
            >
              Forgot?
            </Link>
          </div>
          <Input
            data-testid="login-password"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="rounded-none bg-[#05050A] border-[#232330] focus:border-cyan-400 focus:ring-cyan-400/40"
          />
        </div>
        <Button
          type="submit"
          disabled={busy}
          data-testid="login-submit"
          className="w-full rounded-none bg-cyan-400 hover:bg-cyan-300 text-black font-bold uppercase tracking-widest"
        >
          {busy ? "Authenticating…" : "Enter the arena"}
        </Button>
        <div className="text-sm text-gray-500 text-center">
          No account?{" "}
          <Link to="/register" className="text-cyan-400 hover:underline" data-testid="link-register">
            Register
          </Link>
        </div>
      </form>
    </div>
  );
}
